// src/pages/BillsListPage.tsx
import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router'
import { SgdsButton, SgdsSelect, SgdsSelectOption } from '@govtechsg/sgds-web-component/react'
import { api } from '../lib/api'
import { useApi } from '../lib/useApi'
import { useSort, sortParams } from '../lib/useSort'
import { usePager } from '../lib/usePager'
import { BillStatusBadge, BillTypeBadge } from '../components/StatusBadge'
import { SortableTh, SortAnnouncement } from '../components/SortableTh'
import { EmptyCard, ErrorCard, LoadingCard } from '../components/StateCards'
import { Money } from '../components/Money'
import { BILL_STATUSES, type Bill } from '../lib/types'
import { billStatusLabel, formatIsoDate } from '../lib/format'
import { targetValue } from '../lib/sgds-dom'

const ALL = 'ALL'

/**
 * The columns `GET /api/bills?sort=` accepts — mirrors
 * `BILL_SORT_COLUMNS` in `packages/persistence/src/bill-repository.ts`
 * exactly. Every column shown in the table below is server-sortable, so
 * every header here is a `SortableTh`; if a future column is NOT added to
 * that server-side whitelist, it must render as a plain `<th>` instead — a
 * header that looks sortable and silently does nothing is worse than one
 * that never claimed to be.
 */
type BillSortKey = 'reference' | 'agencyCode' | 'billType' | 'serviceDate' | 'amount' | 'status'

const COLUMN_LABELS: Readonly<Record<BillSortKey, string>> = {
  reference: 'Reference',
  agencyCode: 'Agency',
  billType: 'Type',
  serviceDate: 'Service date',
  amount: 'Amount',
  status: 'Status',
}

export function BillsListPage() {
  const { sort, onSort, interacted } = useSort<BillSortKey>()
  const pager = usePager()

  /**
   * ★★★ WAVE 15 — THE STATUS FILTER LIVES IN THE URL, because §12.84(b)'s
   * drill-down has to land somewhere.
   *
   * The dashboard's lifecycle chart links to `/bills?status=APPROVED`. Held in
   * `useState` alone, that parameter was read by nobody: the link navigated, the
   * page mounted with the filter at ALL, and the reader was shown the whole
   * estate while the address bar claimed a filter. **A screen whose URL and whose
   * state disagree is worse than one with no deep link at all**, so both halves
   * are here — the URL seeds the control, and changing the control rewrites the
   * URL.
   *
   * ⚠ `replace: true` on the write. A filter change is not a navigation an
   * operator wants to walk back through one status at a time; Back should leave
   * the list, which is what it did before this parameter existed.
   *
   * ⚠ An UNKNOWN status in the URL falls back to ALL rather than being sent to
   * the gateway, which would answer 400 and surface as an error on a screen where
   * the reader did nothing wrong — they followed a stale link.
   */
  const [searchParams, setSearchParams] = useSearchParams()
  const requestedStatus = searchParams.get('status')
  const statusFilter =
    requestedStatus !== null && (BILL_STATUSES as readonly string[]).includes(requestedStatus)
      ? requestedStatus
      : ALL
  const setStatusFilter = (next: string): void => {
    setSearchParams(
      (previous) => {
        const params = new URLSearchParams(previous)
        if (next === ALL) params.delete('status')
        else params.set('status', next)
        return params
      },
      { replace: true },
    )
  }
  const [agencyFilter, setAgencyFilter] = useState<string>(ALL)

  // ★ TASK 5D — a cursor is only valid for the sort/dir it was minted under
  // (the gateway refuses a mismatched one with 400); changing sort must
  // restart paging at page 1, never carry a stale cursor into the new order.
  //
  // ★★★ WAVE 11 — AND THE STATUS FILTER IS NOW THE SAME KIND OF THING. A keyset
  // cursor is a position within one population, so `?status=` is part of the
  // contract it was minted under and the gateway refuses a mismatch with the same
  // 400. Without this reset, changing the status control on page 3 would send a
  // stale cursor and surface as an error message on a screen where the user did
  // nothing wrong.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => pager.reset(), [sort.column, sort.dir, statusFilter])

  const bills = useApi(
    () =>
      api.bills({
        ...sortParams(sort),
        ...(pager.cursor !== undefined && { cursor: pager.cursor }),
        // ★★★ WAVE 11 — SERVER-SIDE. This control used to narrow the rows already
        // loaded, and its own hint text said so; on a scope of 5,125 bills that
        // meant "the APPROVED bills among the 25 on this page", under a `total`
        // counting all of them. Now the server filters and counts the same
        // population, so `total` and the paging both describe what is on screen.
        ...(statusFilter !== ALL && { status: [statusFilter] }),
      }),
    [sort.column, sort.dir, pager.cursor, statusFilter],
  )

  const rows: readonly Bill[] = bills.data?.bills ?? []
  const total = bills.data?.total ?? 0
  const nextCursor = bills.data?.nextCursor ?? null
  const agencies = useMemo(() => Array.from(new Set(rows.map((b) => b.agencyCode))).sort(), [rows])

  // ★★★ WAVE 11 — ONLY THE AGENCY FILTER IS CLIENT-SIDE NOW, and the asymmetry is
  // deliberate rather than half-finished work.
  //
  // Status went to the server because its domain is fixed by a database CHECK — six
  // values, known to the build, so the route can REFUSE an unknown one. Agency is
  // different: the options in that control are derived from `rows` (the agencies
  // present on this page), so it is already a filter over a sample by construction,
  // and sending it to the server would need a scope-aware list of the agencies a
  // caller may see before the control could offer a complete set of choices. That
  // is its own change; the control's hint text still says what it does.
  const filtered = rows.filter((b) => agencyFilter === ALL || b.agencyCode === agencyFilter)

  const hasFilter = statusFilter !== ALL || agencyFilter !== ALL
  const clearFilters = () => {
    setStatusFilter(ALL)
    setAgencyFilter(ALL)
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-3xl mb-2">Bills</h1>
        <p className="text-[var(--sgds-body-color-subtle)] max-w-[70ch]">
          Filtered to your access: this list can only ever show bills your own grants cover.
        </p>
      </div>

      {/*
        The same `.pt-controlbar` as the audit trail's, swept here in the same
        pass rather than waiting to be reported separately. This row had the
        identical latent defect — `items-end` across cells of unequal height —
        and it had simply not surfaced because neither select carried hint text
        yet. Adding the one below would have broken it in exactly the way the
        audit bar was broken.

        `pt-filterbar` is gone from the class list: it had no layout rule
        anywhere in the stylesheet, so it was a name that looked like a control
        and was not.
      */}
      <div className="pt-card pt-controlbar">
        <SgdsSelect
          label="Status"
          hintText="Asks the server for this status only: the count and the paging below follow it."
          value={statusFilter}
          onSgdsChange={(e) => setStatusFilter(targetValue(e))}
        >
          <SgdsSelectOption value={ALL}>All statuses</SgdsSelectOption>
          {BILL_STATUSES.map((status) => (
            <SgdsSelectOption key={status} value={status}>
              {billStatusLabel(status)}
            </SgdsSelectOption>
          ))}
        </SgdsSelect>
        <SgdsSelect label="Agency" value={agencyFilter} onSgdsChange={(e) => setAgencyFilter(targetValue(e))}>
          <SgdsSelectOption value={ALL}>All agencies</SgdsSelectOption>
          {agencies.map((code) => (
            <SgdsSelectOption key={code} value={code}>
              {code}
            </SgdsSelectOption>
          ))}
        </SgdsSelect>
        {hasFilter && (
          <div className="pt-controlbar-action">
            <SgdsButton variant="outline" type="button" onClick={clearFilters}>
              Clear filters
            </SgdsButton>
          </div>
        )}
      </div>

      <SortAnnouncement sort={sort} interacted={interacted} busy={bills.loading} columnLabels={COLUMN_LABELS} rowCount={filtered.length} />

      {bills.error !== null ? (
        <ErrorCard title="Could not load bills" message={bills.error} onRetry={bills.reload} />
      ) : bills.initialLoading ? (
        <LoadingCard label="Loading bills" rows={6} />
      ) : rows.length === 0 ? (
        <EmptyCard title="No bills in scope" message="Nothing has happened yet for the agencies and bill types your active identity's grants cover." />
      ) : filtered.length === 0 ? (
        <EmptyCard
          title={
            agencyFilter === ALL ? 'No bills match this status' : 'No bills on this page match this filter'
          }
          /*
            ★★★ WAVE 11 — TWO DIFFERENT SENTENCES NOW, because the two filters stopped
            being the same kind of thing.

            ⚠ This message used to say, of both filters, *"The filters narrow only
            this page; sorting re-queries the server over your whole scope"* — and it
            was carefully correct when the status filter ran in the browser. It is now
            FALSE for status: `?status=` narrows the query and `total`, so an empty
            result IS a statement about the whole scope and can be said as one.
            Leaving the old wording would be a screen making a false statement about
            its own behaviour, which is the S-073 shape this repository logs, and
            worse than the gap it replaced because it tells an officer to go paging
            for something the server has already told them is not there.

            ⚠ AGENCY is still client-side (its options are derived from the rows on
            this page), so when it is engaged the caveat still applies and is still
            said. The two cases are separated rather than covered by one sentence that
            has to be true of both — which is how the old one ended up true of
            neither.
          */
          message={
            agencyFilter === ALL
              ? `No bill in your scope has status "${billStatusLabel(statusFilter)}". The server counted your whole scope to answer this (${total} bill${total === 1 ? '' : 's'}), not just this page, so there is nothing to page through.`
              : `None of the ${rows.length} bills on this page (page ${pager.pageNumber}) are at agency ${agencyFilter}. The AGENCY filter narrows only this page (its choices come from the rows already loaded), so page through, or clear it. The status filter, by contrast, asks the server.`
          }
          onClear={clearFilters}
        />
      ) : (
        <div className="pt-card p-0 overflow-hidden">
          {/* Mounted through a re-sort — see `useApi.initialLoading`. Unmounting
              here is what dropped keyboard focus off the header button. */}
          <div className="pt-table-wrap" aria-busy={bills.loading}>
            <table className="pt-table">
              <caption className="sr-only">
                Bills in your scope{statusFilter !== ALL ? ` with status "${billStatusLabel(statusFilter)}"` : ''}
                {agencyFilter !== ALL ? ` at agency ${agencyFilter}` : ''}, page {pager.pageNumber}
              </caption>
              <thead>
                <tr>
                  <SortableTh label="Reference" sortKey="reference" sort={sort} onSort={onSort} />
                  <SortableTh label="Agency" sortKey="agencyCode" sort={sort} onSort={onSort} />
                  <SortableTh label="Type" sortKey="billType" sort={sort} onSort={onSort} />
                  <SortableTh label="Service date" sortKey="serviceDate" sort={sort} onSort={onSort} />
                  <SortableTh label="Amount" sortKey="amount" sort={sort} onSort={onSort} align="right" />
                  <SortableTh label="Status" sortKey="status" sort={sort} onSort={onSort} />
                </tr>
              </thead>
              <tbody>
                {filtered.map((bill) => (
                  <tr key={bill.reference}>
                    <td>
                      <Link to={`/bills/${bill.reference}`} className="pt-mono font-semibold pt-ref-link">
                        {bill.reference}
                      </Link>
                    </td>
                    <td>{bill.agencyCode}</td>
                    <td><BillTypeBadge billType={bill.billType} /></td>
                    <td className="pt-tabular">{formatIsoDate(bill.serviceDate)}</td>
                    <td className="pt-num">
                      <Money cents={bill.amount} />
                    </td>
                    <td>
                      <BillStatusBadge status={bill.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/*
            ★ TASK 5D. `total` is the gateway's own scope-filtered count — the
            SAME predicate as `rows`, never a wider one (see
            `PostgresBillRepository.list`'s own header) — so this sentence is
            honest about the WHOLE population, not only what happened to load.

            ★★★ WAVE 11 — AND WHAT "THE WHOLE POPULATION" MEANS NOW DEPENDS ON THE
            STATUS FILTER, which is why the wording changed. `?status=` narrows
            `total` as well as the rows, so with a status chosen this figure is the
            count of bills WITH THAT STATUS in scope — and calling it "bills total in
            your scope" would have been a straightforwardly wrong number on screen.
            The AGENCY filter is still client-side, so `filtered.length` is still a
            page-relative count and still says so.
          */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-5 py-3 border-t border-[var(--sgds-border-color-muted)]">
            <p className="text-sm text-[var(--sgds-color-subtle)]">
              Showing {filtered.length} of {rows.length} on this page
              {agencyFilter !== ALL ? ` after filtering to agency ${agencyFilter}` : ''}: page {pager.pageNumber} of{' '}
              {total}{' '}
              {statusFilter === ALL
                ? `bill${total === 1 ? '' : 's'} in your scope.`
                : `"${billStatusLabel(statusFilter)}" bill${total === 1 ? '' : 's'} in your scope, counted by the server.`}{' '}
              {sort.column === null
                ? 'The server default order.'
                : `Sorted on the server by ${COLUMN_LABELS[sort.column]} (${sort.dir === 'asc' ? 'ascending' : 'descending'}) over your whole scope, so paging never reshuffles a page you have already seen.`}
            </p>
            <div className="flex items-center gap-2 shrink-0">
              <SgdsButton
                variant="outline"
                size="sm"
                type="button"
                disabled={!pager.canGoPrevious || bills.loading}
                onClick={pager.goPrevious}
              >
                Previous
              </SgdsButton>
              <SgdsButton
                variant="outline"
                size="sm"
                type="button"
                disabled={nextCursor === null || bills.loading}
                onClick={() => {
                  if (nextCursor !== null) pager.goNext(nextCursor)
                }}
              >
                Next
              </SgdsButton>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
